import type { FC, ReactNode } from 'react'

import { cn } from '@/lib/utils'

interface SectionHeadingProps {
    eyebrow?: string
    title: ReactNode
    intro?: ReactNode
    align?: 'left' | 'center'
    as?: 'h1' | 'h2'
    tone?: 'default' | 'light'
    className?: string
}

export const SectionHeading: FC<SectionHeadingProps> = ({
    eyebrow,
    title,
    intro,
    align = 'left',
    as: Heading = 'h2',
    tone = 'default',
    className
}): ReactNode => {
    const isLight = tone === 'light'

    return (
        <div className={cn(align === 'center' ? 'mx-auto max-w-3xl text-center' : 'max-w-2xl', className)}>
            {eyebrow && (
                <p className={cn('mb-4 inline-flex items-center gap-3 text-sm font-semibold uppercase tracking-[0.18em]', isLight ? 'text-white' : 'text-plum')}>
                    <span className='h-px w-8 bg-honey' aria-hidden='true' />
                    {eyebrow}
                </p>
            )}

            <Heading
                className={cn(
                    'font-heading font-normal text-balance',
                    Heading === 'h1' ? 'text-4xl leading-[1.08] sm:text-5xl lg:text-6xl' : 'text-3xl leading-tight sm:text-4xl lg:text-[2.75rem]',
                    isLight ? 'text-white' : 'text-plum'
                )}
            >
                {title}
            </Heading>

            {intro && (
                <div className={cn('mt-5 text-lg leading-relaxed text-pretty', isLight ? 'text-white/85' : 'text-espresso-soft')}>
                    {intro}
                </div>
            )}
        </div>
    )
}
